import React, { useEffect, useMemo, useLayoutEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import CrossIcon from '@atlaskit/icon/core/cross';
import { useThemeObserver } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Inline } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button';

import { ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useWindowSize } from '@confluence/dom-helpers';

import { getAdjustedTopPropertyValue } from './utils/getAdjustedTopPropertyValue.ts';
import { TemplateBannerIllustration } from './TemplateBannerIllustration/TemplateBannerIllustration.tsx';
import {
	TemplateBannerContainer,
	IconContainer,
	StyledButton,
	TitleWrapper,
	BodyWrapper,
	LeftWrapper,
	RightWrapper,
	templateSidebarWidth,
} from './styled/index.ts';
import type { EditorBannerProps, EditorBannerContainerProps } from './OnboardingEditorBanner.tsx';
import { useOnboardingEditorActions } from './useOnboardingEditorState.tsx';
import LandInEditorBannerBackgroundLight from './assets/LandInEditorBannerBackgroundLight.svg';
import LandInEditorBannerBackgroundDark from './assets/LandInEditorBannerBackgroundDark.svg';

const i18n = defineMessages({
	bannerTitle: {
		id: 'onboarding-editor.land-in-editor.banner.title',
		defaultMessage: 'There’s a template for that',
		description:
			'This is the title of the land in editor banner. It appears as a heading to introduce template recommendations to users when they start creating a new page.',
	},
	bannerDescription: {
		id: 'onboarding-editor.land-in-editor.banner.description',
		defaultMessage:
			'Based on your responses, here’s a template best suited to help you get started.',
		description:
			'This is the body text of the land in editor banner. It explains that the recommended template is personalized based on user responses and will help them start creating their page.',
	},
	exploreCTA: {
		id: 'onboarding-editor.land-in-editor.explore-templates',
		defaultMessage: 'Explore all templates',
		description:
			'This is the text on a button in the land in editor banner. When clicked, it opens the template selection panel so users can browse and choose from all available templates.',
	},
	blankPageCTA: {
		id: 'onboarding-editor.land-in-editor.blank-page',
		defaultMessage: 'Start with a blank page',
		description: 'This message refers to starting with a Confluence page that has no content',
	},
});

export const LandInEditorBanner = (
	props: EditorBannerProps & EditorBannerContainerProps,
): React.JSX.Element => {
	const {
		isContentCreationPanelOpen,
		showingOfflineBanner,
		setIsVisible,
		deserializedData: { isLandInEditorBannerVisible } = {},
		setIsOnboardingBannerDataLoaded,
		editorActions,
		onClickOpenContentCreationPanel,
		handleTitleChange,
	} = props;

	const { setHasLandInEditorBannerVisible } = useOnboardingEditorActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	useEffect(() => {
		if (isLandInEditorBannerVisible) {
			void setOnboardingState([
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.IS_LAND_IN_EDITOR_BANNER_VISIBLE,
					value: 'false',
				},
			]);

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'landInEditorBanner',
				},
			}).fire();
			setHasLandInEditorBannerVisible(true);
		}
		// TODO: Refactor https://hello.atlassian.net/browse/CONFONBO-1674
		setTimeout(() => {
			setIsOnboardingBannerDataLoaded(true);
		}, 0);
	}, [
		setOnboardingState,
		isLandInEditorBannerVisible,
		createAnalyticsEvent,
		setIsOnboardingBannerDataLoaded,
		setHasLandInEditorBannerVisible,
	]);

	const { width: windowWidth } = useWindowSize();

	// Apply margin-top with useLayoutEffect (before paint). This prevents TTVC mutations.
	useLayoutEffect(() => {
		const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

		// shift the editor content down for different banner breakpoints
		if (editorNode) {
			if (windowWidth <= 375) {
				editorNode.style.marginTop = '300px';
			} else if (
				windowWidth <= 1024 ||
				(isContentCreationPanelOpen && windowWidth <= 1024 + templateSidebarWidth)
			) {
				editorNode.style.marginTop = '291px';
			} else {
				editorNode.style.marginTop = '174px';
			}
		}
		return () => {
			if (editorNode) editorNode.style.marginTop = '0';
		};
	}, [windowWidth, isContentCreationPanelOpen]);

	const handleExploreCTA = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lieBannerExploreTemplates',
				source: 'landInEditorBanner',
			},
		}).fire();
		onClickOpenContentCreationPanel();
		setIsVisible(false);
	};

	const handleBlankPageCTA = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lieBannerBlankPage',
				source: 'landInEditorBanner',
			},
		}).fire();
		onClickOpenContentCreationPanel();
		editorActions.clear();
		setIsVisible(false);
		handleTitleChange('');
	};

	const _onDismiss = () => {
		// this will start the animation, and onAnimationEnd we should hide the component
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lieBannerDismiss',
				source: 'landInEditorBanner',
			},
		}).fire();
		setIsVisible(false);
	};

	// compiled shouldn't handle a complicated runtime CSS, hence the inlined style
	const calculatedTop = useMemo(
		() => getAdjustedTopPropertyValue(showingOfflineBanner),
		[showingOfflineBanner],
	);

	const theme = useThemeObserver();
	const backgroundImageUrl =
		theme.colorMode === 'dark'
			? LandInEditorBannerBackgroundDark
			: LandInEditorBannerBackgroundLight;

	return (
		<>
			<TemplateBannerContainer
				style={{ top: calculatedTop, backgroundImage: `url(${backgroundImageUrl})` }}
				isContentCreationPanelOpen={isContentCreationPanelOpen}
				data-testid="onboarding-land-in-editor-banner"
				fade={false}
			>
				<LeftWrapper>
					<TemplateBannerIllustration />
				</LeftWrapper>
				<RightWrapper isContentCreationPanelOpen={isContentCreationPanelOpen}>
					<TitleWrapper>
						<FormattedMessage {...i18n.bannerTitle} />
					</TitleWrapper>
					<BodyWrapper isContentCreationPanelOpen={isContentCreationPanelOpen}>
						<FormattedMessage {...i18n.bannerDescription} />
					</BodyWrapper>
					<Inline space="space.050">
						<StyledButton
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ width: 'fit-content' }}
							onClick={handleExploreCTA}
						>
							<FormattedMessage {...i18n.exploreCTA} />
						</StyledButton>
						<Button appearance="subtle" onClick={handleBlankPageCTA}>
							<FormattedMessage {...i18n.blankPageCTA} />
						</Button>
					</Inline>
				</RightWrapper>
				<IconContainer data-testid="onboarding-land-in-editor-banner-close" onClick={_onDismiss}>
					<CrossIcon label="dismiss" />
				</IconContainer>
			</TemplateBannerContainer>
			<ExperienceSuccess name={ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE} />
		</>
	);
};
